import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  AlertTriangle, 
  Check, 
  X, 
  Edit2, 
  Save, 
  RefreshCw, 
  FileText,
  History,
  Shield,
  AlertCircle,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { getApiBaseUrl } from '../lib/api';

const API_URL = getApiBaseUrl();

const DocuSignTemplateAdmin = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [changeReason, setChangeReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);

  const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/docusign-templates?include_inactive=${includeInactive}`,
        {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
        setSummary(data.summary || null);
      } else {
        toast.error('Failed to load templates');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Error loading templates');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  const fetchAuditLog = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/docusign-templates/audit/log?limit=50`,
        {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs || []);
        setShowAuditLog(true);
      }
    } catch (error) {
      toast.error('Failed to load audit log');
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleFieldChange = (documentType, field, value) => {
    setPendingChanges(prev => ({
      ...prev,
      [documentType]: {
        ...(prev[documentType] || {}),
        [field]: value
      }
    }));
  };

  const hasChanges = Object.keys(pendingChanges).length > 0;

  const handleSaveChanges = async () => {
    if (!changeReason || changeReason.length < 10) {
      toast.error('Please provide a change reason (minimum 10 characters)');
      return;
    }

    setSaving(true);
    try {
      const updates = Object.entries(pendingChanges).map(([documentType, changes]) => ({
        document_type: documentType,
        ...changes
      }));

      const response = await fetch(`${API_URL}/api/admin/docusign-templates/bulk-update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          updates,
          change_reason: changeReason,
          confirm: true
        })
      });

      if (response.ok) {
        toast.success('Templates updated successfully');
        setPendingChanges({});
        setChangeReason('');
        setShowConfirmDialog(false);
        fetchTemplates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save changes');
      }
    } catch (error) {
      toast.error('Error saving changes');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (template) => {
    if (!template.is_active) {
      return <Badge variant="secondary" className="bg-gray-100 text-gray-600">Inactive</Badge>;
    }
    if (template.template_id) {
      return <Badge className="bg-green-100 text-green-700">Configured</Badge>;
    }
    return <Badge variant="outline" className="border-amber-500 text-amber-600">Not Configured</Badge>;
  };

  const getPendingValue = (documentType, field, originalValue) => {
    if (pendingChanges[documentType]?.[field] !== undefined) {
      return pendingChanges[documentType][field];
    }
    return originalValue;
  };

  const isFieldChanged = (documentType, field, originalValue) => {
    return pendingChanges[documentType]?.[field] !== undefined && 
           pendingChanges[documentType][field] !== originalValue;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="docusign-template-admin">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">DocuSign Template Management</h1>
            <p className="text-slate-600 mt-1">Configure e-signature templates for documents</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={fetchAuditLog}
              data-testid="view-audit-log"
            >
              <History className="h-4 w-4 mr-2" />
              Audit Log
            </Button>
            <Button 
              variant="outline" 
              onClick={fetchTemplates}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{summary.total}</div>
                <div className="text-sm text-slate-600">Total Templates</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-green-600">{summary.configured}</div>
                <div className="text-sm text-slate-600">Configured</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-amber-600">{summary.unconfigured}</div>
                <div className="text-sm text-slate-600">Unconfigured</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-blue-600">{summary.active}</div>
                <div className="text-sm text-slate-600">Active</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-purple-600">{summary.required}</div>
                <div className="text-sm text-slate-600">Required</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pending Changes Warning */}
        {hasChanges && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  <div>
                    <div className="font-semibold text-amber-800">
                      You have unsaved changes ({Object.keys(pendingChanges).length} template(s))
                    </div>
                    <div className="text-sm text-amber-700">
                      Changes will not take effect until you save them.
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setPendingChanges({})}
                  >
                    Discard
                  </Button>
                  <Button 
                    onClick={() => setShowConfirmDialog(true)}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="save-changes-btn"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Templates Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Template Mappings</CardTitle>
                <CardDescription>
                  Configure DocuSign template IDs for each document type
                </CardDescription>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Show inactive
              </label>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Document Type</TableHead>
                      <TableHead>Template Name</TableHead>
                      <TableHead className="w-[300px]">DocuSign Template ID</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Signers</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Required</TableHead>
                      <TableHead>Active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {templates.map((template) => (
                      <TableRow 
                        key={template.id}
                        className={pendingChanges[template.document_type] ? 'bg-amber-50' : ''}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-slate-400" />
                            <span className="text-xs font-mono">{template.document_type}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={getPendingValue(template.document_type, 'template_name', template.template_name || '')}
                            onChange={(e) => handleFieldChange(template.document_type, 'template_name', e.target.value)}
                            className={`h-8 ${isFieldChanged(template.document_type, 'template_name', template.template_name) ? 'border-amber-500 bg-amber-50' : ''}`}
                            placeholder="Template name..."
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={getPendingValue(template.document_type, 'template_id', template.template_id || '')}
                            onChange={(e) => handleFieldChange(template.document_type, 'template_id', e.target.value)}
                            className={`h-8 font-mono text-xs ${isFieldChanged(template.document_type, 'template_id', template.template_id) ? 'border-amber-500 bg-amber-50' : ''}`}
                            placeholder="Enter DocuSign template ID..."
                          />
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{template.entity_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {template.required_signers?.map((signer, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {signer}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(template)}</TableCell>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={getPendingValue(template.document_type, 'is_required', template.is_required)}
                            onChange={(e) => handleFieldChange(template.document_type, 'is_required', e.target.checked)}
                            className={`rounded ${isFieldChanged(template.document_type, 'is_required', template.is_required) ? 'ring-2 ring-amber-500' : ''}`}
                          />
                        </TableCell>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={getPendingValue(template.document_type, 'is_active', template.is_active)}
                            onChange={(e) => handleFieldChange(template.document_type, 'is_active', e.target.checked)}
                            className={`rounded ${isFieldChanged(template.document_type, 'is_active', template.is_active) ? 'ring-2 ring-amber-500' : ''}`}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <div className="font-semibold mb-1">Template ID Configuration</div>
                <p>
                  To obtain a Template ID, create a template in your DocuSign Developer Console,
                  then copy the Template ID from the template settings. Changes here affect how
                  documents are sent for e-signature across the platform.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Confirm Save Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
                Confirm Template Changes
              </DialogTitle>
              <DialogDescription>
                You are about to update {Object.keys(pendingChanges).length} template(s).
                These changes will affect e-signature workflows across the platform.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Warning Box */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800">
                    <div className="font-semibold">Warning</div>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Invalid template IDs will cause e-signature failures</li>
                      <li>Deactivating required templates may block workflows</li>
                      <li>All changes are logged for audit purposes</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Changes Summary */}
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-sm font-medium mb-2">Changes to be applied:</div>
                <div className="space-y-1 text-sm">
                  {Object.entries(pendingChanges).map(([docType, changes]) => (
                    <div key={docType} className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-xs">{docType}</Badge>
                      <span className="text-slate-600">
                        {Object.keys(changes).join(', ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Change Reason */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Reason for changes <span className="text-red-500">*</span>
                </label>
                <Textarea
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value)}
                  placeholder="Explain why you are making these changes (min 10 characters)..."
                  className={changeReason.length < 10 ? 'border-red-300' : ''}
                  rows={3}
                  data-testid="change-reason-input"
                />
                {changeReason.length < 10 && changeReason.length > 0 && (
                  <p className="text-xs text-red-500 mt-1">
                    Minimum 10 characters required ({changeReason.length}/10)
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setShowConfirmDialog(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={saving || changeReason.length < 10}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-save-btn"
              >
                {saving ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Confirm & Save Changes
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Audit Log Dialog */}
        <Dialog open={showAuditLog} onOpenChange={setShowAuditLog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Template Change Audit Log
              </DialogTitle>
              <DialogDescription>
                History of all template configuration changes
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              {auditLogs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No audit log entries found
                </div>
              ) : (
                <div className="space-y-3">
                  {auditLogs.map((log) => (
                    <div 
                      key={log.id} 
                      className="border rounded-lg p-3 text-sm"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant={log.action === 'updated' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {log.action}
                          </Badge>
                          <span className="font-mono text-xs text-slate-600">
                            {log.document_type}
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">
                          {new Date(log.created_at).toLocaleString()}
                        </span>
                      </div>
                      {log.changed_by_email && (
                        <div className="text-xs text-slate-600 mb-1">
                          By: {log.changed_by_email}
                        </div>
                      )}
                      {log.change_reason && (
                        <div className="text-xs bg-slate-50 rounded p-2 mt-2">
                          <strong>Reason:</strong> {log.change_reason}
                        </div>
                      )}
                      {log.new_values && (
                        <div className="text-xs mt-2">
                          <strong>Changes:</strong>{' '}
                          <code className="bg-slate-100 px-1 rounded">
                            {JSON.stringify(log.new_values)}
                          </code>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAuditLog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default DocuSignTemplateAdmin;
